import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface SelectedAddon {
  addonId: string;
  name: string;
  price: number;
  quantity: number;
}

export interface CartItem {
  productId: string;
  variantId?: string;
  quantity: number;
  product: {
    _id: string;
    name: string;
    price: number;
    salePrice?: number;
    currency?: string;
    primaryImage?: string;
    images?: Array<{ url: string; isPrimary?: boolean }>;
    brand?: string;
    sku?: string;
    availability?: 'in_stock' | 'out_of_stock' | 'preorder' | 'backorder';
    hasVariants?: boolean;
    inventoryTracking?: boolean;
    inventoryQuantity?: number;
    /** Least quantity an order may contain (all variants together). */
    minimumOrderQuantity?: number;
    addons?: Array<{
      id: string;
      name: string;
      price: number;
      description?: string;
      isRequired?: boolean;
      maxQuantity?: number;
    }>;
  };
  variant?: {
    id: string;
    name: string;
    price?: number;
    inventoryQuantity?: number;
  };
  selectedAddons?: SelectedAddon[];
}

export interface CartState {
  /** Business the cart's items belong to — a cart never mixes stores. */
  businessId?: string;
  items: CartItem[];
  totalItems: number;
  totalPrice: number;

  // Actions
  /** Adds one more of the item — or `initialQuantity` (e.g. the product's
   * minimum order quantity) when it isn't in the cart yet. */
  addItem: (item: Omit<CartItem, 'quantity'>, initialQuantity?: number) => void;
  removeItem: (productId: string, variantId?: string) => void;
  updateQuantity: (productId: string, variantId: string | undefined, quantity: number) => void;
  updateAddons: (productId: string, variantId: string | undefined, addons: SelectedAddon[]) => void;
  getItemQuantity: (productId: string, variantId?: string) => number;
  clearCart: () => void;
  getCartItem: (productId: string, variantId?: string) => CartItem | undefined;
  /** Switches the cart to this business, emptying it if it held another store's items. */
  ensureBusiness: (businessId: string) => void;
  /** Opened from a conversation's link: the cart mirrors that conversation's shared cart (the
   * AI and the team work on the same one) — see sharedCartSync.ts. Its priced view (taxes and
   * charges) and the customer's cart on another number, when there is one. */
  shared?: { conversationId: string; priced?: SharedCartPriced; otherCart?: SharedOtherCart };
  replaceItems: (items: CartItem[]) => void;
  setShared: (shared: CartState['shared']) => void;
}

export interface SharedCartPriced {
  subtotal: number;
  taxes: Array<{ name: string; rate: number; amount: number }>;
  charges: Array<{ name: string; amount: number }>;
  total: number;
  currency: string;
}
export interface SharedOtherCart { conversationId: string; items: number; total: number; currency: string }

const getCartItemKey = (productId: string, variantId?: string): string =>
  variantId ? `${productId}_${variantId}` : productId;

const computeItemPrice = (item: CartItem): number => {
  const base = item.variant?.price ?? item.product.salePrice ?? item.product.price ?? 0;
  const addonsTotal = (item.selectedAddons || []).reduce(
    (s, a) => s + a.price * a.quantity, 0
  );
  return (base + addonsTotal) * item.quantity;
};

const recompute = (items: CartItem[]) => ({
  totalItems: items.reduce((s, i) => s + i.quantity, 0),
  totalPrice: items.reduce((s, i) => s + computeItemPrice(i), 0),
});

export const useCartStore = create<CartState>()(
  persist(
    (set, get) => ({
      items: [],
      totalItems: 0,
      totalPrice: 0,

      addItem: (item, initialQuantity) => {
        const state = get();
        const key = getCartItemKey(item.productId, item.variantId);
        const existing = state.items.find(
          (i) => getCartItemKey(i.productId, i.variantId) === key
        );

        if (existing) {
          get().updateQuantity(item.productId, item.variantId, existing.quantity + 1);
          // Re-adding with a different add-on selection keeps the latest one.
          if (item.selectedAddons !== undefined) {
            get().updateAddons(item.productId, item.variantId, item.selectedAddons);
          }
        } else {
          const newItem: CartItem = { ...item, quantity: Math.max(1, Math.floor(initialQuantity || 1)) };
          set((state) => {
            const newItems = [...state.items, newItem];
            return { items: newItems, ...recompute(newItems) };
          });
        }
      },

      removeItem: (productId, variantId) => {
        set((state) => {
          const key = getCartItemKey(productId, variantId);
          const newItems = state.items.filter(
            (i) => getCartItemKey(i.productId, i.variantId) !== key
          );
          return { items: newItems, ...recompute(newItems) };
        });
      },

      updateQuantity: (productId, variantId, quantity) => {
        if (quantity <= 0) {
          get().removeItem(productId, variantId);
          return;
        }
        set((state) => {
          const key = getCartItemKey(productId, variantId);
          const newItems = state.items.map((item) =>
            getCartItemKey(item.productId, item.variantId) === key
              ? { ...item, quantity }
              : item
          );
          return { items: newItems, ...recompute(newItems) };
        });
      },

      updateAddons: (productId, variantId, addons) => {
        set((state) => {
          const key = getCartItemKey(productId, variantId);
          const newItems = state.items.map((item) =>
            getCartItemKey(item.productId, item.variantId) === key
              ? { ...item, selectedAddons: addons }
              : item
          );
          return { items: newItems, ...recompute(newItems) };
        });
      },

      getItemQuantity: (productId, variantId) => {
        const key = getCartItemKey(productId, variantId);
        const item = get().items.find(
          (i) => getCartItemKey(i.productId, i.variantId) === key
        );
        return item?.quantity ?? 0;
      },

      getCartItem: (productId, variantId) => {
        const key = getCartItemKey(productId, variantId);
        return get().items.find(
          (i) => getCartItemKey(i.productId, i.variantId) === key
        );
      },

      clearCart: () => set({ items: [], totalItems: 0, totalPrice: 0 }),

      replaceItems: (items) => set({ items, ...recompute(items) }),

      setShared: (shared) => set({ shared }),

      ensureBusiness: (businessId) => {
        const current = get().businessId;
        if (current === businessId) return;
        // An existing cart from before scoping (no businessId) is adopted;
        // one from a different store is cleared.
        if (current && current !== businessId) set({ businessId, items: [], totalItems: 0, totalPrice: 0 });
        else set({ businessId });
      },
    }),
    { name: 'sendifier-cart-storage' }
  )
);
